import { gql } from "@apollo/client";

// Start Message fragments
export const START_MESSAGE_FRAGMENT = gql`
  fragment StartMessageFields on StartMessage {
    id
    name
    content
    isActive
    usageCount
    tags
    conversationIds
    createdAt
    updatedAt
    version
  }
`;

export const START_MESSAGE_WITH_TRACKING_FRAGMENT = gql`
  fragment StartMessageWithTrackingFields on StartMessageWithTracking {
    message {
      ...StartMessageFields
    }
    trackingData
    fullContent
    trackingLink
    previewSMS
    previewChat
  }
  ${START_MESSAGE_FRAGMENT}
`;

// Queries
export const LIST_START_MESSAGES = gql`
  query ListStartMessages(
    $filter: StartMessageFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listStartMessages(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        ...StartMessageFields
      }
      nextToken
      total
    }
  }
  ${START_MESSAGE_FRAGMENT}
`;

export const GET_START_MESSAGE = gql`
  query GetStartMessage($id: ID!) {
    getStartMessage(id: $id) {
      ...StartMessageFields
    }
  }
  ${START_MESSAGE_FRAGMENT}
`;

export const GENERATE_START_MESSAGE_WITH_TRACKING = gql`
  query GenerateStartMessageWithTracking(
    $messageId: ID!
    $conversationId: ID!
    $userId: ID!
  ) {
    generateStartMessageWithTracking(
      messageId: $messageId
      conversationId: $conversationId
      userId: $userId
    ) {
      ...StartMessageWithTrackingFields
    }
  }
  ${START_MESSAGE_WITH_TRACKING_FRAGMENT}
`;

// Mutations
export const CREATE_START_MESSAGE = gql`
  mutation CreateStartMessage($input: CreateStartMessageInput!) {
    createStartMessage(input: $input) {
      ...StartMessageFields
    }
  }
  ${START_MESSAGE_FRAGMENT}
`;

export const UPDATE_START_MESSAGE = gql`
  mutation UpdateStartMessage($input: UpdateStartMessageInput!) {
    updateStartMessage(input: $input) {
      ...StartMessageFields
    }
  }
  ${START_MESSAGE_FRAGMENT}
`;

export const DELETE_START_MESSAGE = gql`
  mutation DeleteStartMessage($id: ID!) {
    deleteStartMessage(id: $id)
  }
`;

export const ATTACH_START_MESSAGE_TO_CONVERSATION = gql`
  mutation AttachStartMessageToConversation(
    $messageId: ID!
    $conversationId: ID!
  ) {
    attachStartMessageToConversation(
      messageId: $messageId
      conversationId: $conversationId
    ) {
      ...StartMessageFields
    }
  }
  ${START_MESSAGE_FRAGMENT}
`;

export const DETACH_START_MESSAGE_FROM_CONVERSATION = gql`
  mutation DetachStartMessageFromConversation(
    $messageId: ID!
    $conversationId: ID!
  ) {
    detachStartMessageFromConversation(
      messageId: $messageId
      conversationId: $conversationId
    ) {
      ...StartMessageFields
    }
  }
  ${START_MESSAGE_FRAGMENT}
`;

// Subscriptions
export const ON_START_MESSAGE_UPDATED = gql`
  subscription OnStartMessageUpdated($tenantId: ID!) {
    onStartMessageUpdated(tenantId: $tenantId) {
      ...StartMessageFields
    }
  }
  ${START_MESSAGE_FRAGMENT}
`;

export const ON_START_MESSAGE_USAGE_UPDATED = gql`
  subscription OnStartMessageUsageUpdated($messageId: ID!) {
    onStartMessageUsageUpdated(messageId: $messageId) {
      ...StartMessageFields
    }
  }
  ${START_MESSAGE_FRAGMENT}
`;

// Analytics queries
export const GET_START_MESSAGE_ANALYTICS = gql`
  query GetStartMessageAnalytics(
    $messageId: ID!
    $dateFrom: String
    $dateTo: String
  ) {
    getStartMessageAnalytics(
      messageId: $messageId
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      messageId
      totalUsage
      smsUsage
      chatUsage
      deviceBreakdown {
        PHONE
        DESKTOP
        TABLET
        OTHER
      }
      conversationBreakdown {
        conversationId
        usage
      }
      dailyUsage {
        date
        usage
      }
    }
  }
`;

export const GET_CONVERSATION_START_MESSAGE_ANALYTICS = gql`
  query GetConversationStartMessageAnalytics(
    $conversationId: ID!
    $dateFrom: String
    $dateTo: String
  ) {
    getConversationStartMessageAnalytics(
      conversationId: $conversationId
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      conversationId
      totalMessages
      totalUsage
      averageUsagePerMessage
      messageBreakdown {
        messageId
        messageName
        usage
        smsUsage
        chatUsage
      }
    }
  }
`;

export const GET_TENANT_START_MESSAGE_ANALYTICS = gql`
  query GetTenantStartMessageAnalytics($dateFrom: String, $dateTo: String) {
    getTenantStartMessageAnalytics(dateFrom: $dateFrom, dateTo: $dateTo) {
      tenantId
      totalMessages
      totalUsage
      smsUsage
      chatUsage
      deviceBreakdown {
        PHONE
        DESKTOP
        TABLET
        OTHER
      }
      topMessages {
        messageId
        messageName
        usage
      }
    }
  }
`;
